import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import { formatVolumeToThreeDecimals } from "../utils/form_auto_fill_utils";

const REGION_CONFIG = {
  "3mm_full_circle": { name: "3mm Full Circle", color: "#e57373" },
  "5mm_full_circle": { name: "5mm Full Circle", color: "#ffb74d" },
  "6mm_full_circle": { name: "6mm Full Circle", color: "#81c784" },
  CSF: { name: "Central Subfield (CSF)", color: "#ffff00" },
  ISF: { name: "Inner Subfield (ISF)", color: "#ff0000" },
  OSF: { name: "Outer Subfield (OSF)", color: "#00ff00" },
};

const FLUID_CONFIG = {
  IRF: { label: "IRF", color: "#ff0000" },
  PED: { label: "PED", color: "#0000ff" },
  SRF: { label: "SRF", color: "#00ff00" },
  SHRM: { label: "SHRM", color: "#ffff00" },
};

const FLUID_ORDER = ["IRF", "PED", "SRF", "SHRM"];

const REGION_ORDER = [
  "CSF",
  "ISF",
  "OSF",
  "3mm_full_circle",
  "5mm_full_circle",
  "6mm_full_circle",
];

const formatVolume = (value) =>
  formatVolumeToThreeDecimals(value ?? 0) ?? "0.000";

const renderFluidRow = (key, fluidKey, fluidData) => {
  const fluidConfig = FLUID_CONFIG[fluidKey];
  if (!fluidConfig) return null;

  return (
    <Box
      key={key}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: "11px",
        paddingLeft: "24px",
        paddingRight: "8px",
        minHeight: "20px",
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontSize: "11px", fontWeight: "medium", color: fluidConfig.color }}
      >
        {fluidConfig.label}:
      </Typography>
      <Typography variant="caption" sx={{ fontSize: "11px" }}>
        {formatVolume(fluidData?.volume_mm3)} mm³
      </Typography>
    </Box>
  );
};

const getFluidsInVolumesData = (volumesData) => {
  const labels = new Set();
  const regions = volumesData?.regions;
  const totals = volumesData?.totals;

  if (regions) {
    Object.values(regions).forEach((region) => {
      if (!region) return;
      Object.keys(region).forEach((key) => {
        if (FLUID_ORDER.includes(key)) labels.add(key);
      });
    });
  }
  if (totals) {
    Object.keys(totals).forEach((key) => {
      if (FLUID_ORDER.includes(key)) labels.add(key);
    });
  }

  return FLUID_ORDER.filter((key) => labels.has(key));
};

const FluidVolumeMeasurements = ({ volumesData }) => {
  if (!volumesData?.regions) return null;

  const regions = volumesData.regions;
  const totals = volumesData.totals || {};
  const fluidOrder = getFluidsInVolumesData(volumesData);

  return (
    <Box sx={{ marginTop: "8px", marginBottom: "45px" }}>
      <Divider sx={{ margin: "8px 0" }} />
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: "12px",
          fontWeight: "bold",
          paddingLeft: "16px",
          marginBottom: "4px",
          color: "rgba(0,0,0,0.7)",
        }}
      >
        Fluid Volume Measurements
      </Typography>

      {REGION_ORDER.filter((key) => regions[key]).map((regionKey) => {
        const regionData = regions[regionKey];
        const config = REGION_CONFIG[regionKey] || {
          name: regionKey,
          color: "#999",
        };

        return (
          <Box key={regionKey} sx={{ marginBottom: "8px" }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                fontWeight: "bold",
                paddingLeft: "16px",
                color: config.color,
                display: "block",
                marginBottom: "2px",
              }}
            >
              {config.name}
            </Typography>
            {[
              ...fluidOrder.filter((fluidKey) => regionData[fluidKey]),
              ...Object.keys(regionData).filter(
                (fluidKey) => !FLUID_ORDER.includes(fluidKey)
              ),
            ].map((fluidKey) =>
              renderFluidRow(
                `${regionKey}-${fluidKey}`,
                fluidKey,
                regionData[fluidKey]
              )
            )}
          </Box>
        );
      })}

      {Object.keys(totals).length > 0 ? (
        <Box sx={{ marginTop: "8px" }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "11px",
              fontWeight: "bold",
              paddingLeft: "16px",
              color: "rgba(0,0,0,0.7)",
              display: "block",
              marginBottom: "4px",
            }}
          >
            Totals
          </Typography>
          {fluidOrder.filter((fluidKey) => totals[fluidKey])
            .concat(
              Object.keys(totals).filter((fluidKey) => !FLUID_ORDER.includes(fluidKey))
            )
            .map((fluidKey) =>
              renderFluidRow(`total-${fluidKey}`, fluidKey, totals[fluidKey])
            )}
        </Box>
      ) : null}
    </Box>
  );
};

export default FluidVolumeMeasurements;
